import type { ComputedRef, Ref } from "vue";
import { isValidEmail } from "@donoor/validators";
import { concerneTarif } from "@donoor/utils";
import type { EventCustomFieldDef, TicketOption } from "~/types/event";

/** Participant local du checkout /b/[slug] (une entrée par billet sélectionné). */
export interface EventAttendeeLocal {
  firstName: string;
  lastName: string;
  email: string;
  custom_data: Record<string, unknown>;
  ticketTypeId: string;
  ticketTypeName: string;
  selectedOptions: Record<string, number>;
}

export interface UseEventCheckoutValidationDeps {
  attendees: Ref<EventAttendeeLocal[]>;
  eventCustomFields: ComputedRef<EventCustomFieldDef[]>;
  getOptionsForTicketType: (ticketTypeId: string) => TicketOption[];
  buyerInfo: Ref<{
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phone: string;
  }>;
  totalSelected: ComputedRef<number>;
  checkoutStep: Ref<number>;
}

/**
 * useEventCheckoutValidation — validations des étapes du checkout /b/[slug]
 * (participants complets, coordonnées valides, navigation entre steps).
 * Extrait 1:1 de `pages/b/[slug]/index.vue` (Zone A split >800 lignes).
 * Pendant de `useMembershipCheckoutValidation` côté /a.
 */
export function useEventCheckoutValidation(deps: UseEventCheckoutValidationDeps) {
  const {
    attendees,
    eventCustomFields,
    getOptionsForTicketType,
    buyerInfo,
    totalSelected,
    checkoutStep,
  } = deps;

  // Vérifie qu'un participant est complet
  function isAttendeeComplete(a: EventAttendeeLocal): boolean {
    if (!a.firstName || !a.lastName || !a.email || !isValidEmail(a.email)) return false;
    for (const f of eventCustomFields.value) {
      // Question d'un autre tarif : ni posée, ni exigée (tarifs concernés).
      if (!f?.required || !concerneTarif(f, a.ticketTypeId)) continue;
      const v = a.custom_data?.[f.key];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
        return false;
      }
    }
    const requiredOpts = getOptionsForTicketType(a.ticketTypeId).filter((o: TicketOption) => o.is_required);
    if (!requiredOpts.every((o: TicketOption) => (a.selectedOptions[o.id] || 0) > 0)) return false;
    return true;
  }

  const allAttendeesComplete = computed(() => attendees.value.length > 0 && attendees.value.every(isAttendeeComplete));

  const coordsValid = computed(() => {
    return !!(buyerInfo.value.firstName
      && buyerInfo.value.lastName
      && buyerInfo.value.email
      && buyerInfo.value.confirmEmail
      && buyerInfo.value.email === buyerInfo.value.confirmEmail);
  });

  // Max step reachable based on filled data
  const maxReachableStep = computed(() => {
    if (totalSelected.value === 0) return 1;
    if (!allAttendeesComplete.value) return 2;
    if (!coordsValid.value) return 3;
    return 4;
  });

  function canGoToStep(step: number): boolean {
    if (step === checkoutStep.value) return false;
    if (step < checkoutStep.value) return true;
    return step <= maxReachableStep.value;
  }

  return {
    allAttendeesComplete,
    coordsValid,
    maxReachableStep,
    canGoToStep,
  };
}
